import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Music, Headphones, Check, ChevronRight } from 'lucide-react';
import BeatCard from '../components/BeatCard';
import Footer from '../components/Footer';
import { useApp } from '../context/AppContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const HomePage = () => {
  const [beats, setBeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { sectionVisibility } = useApp();

  useEffect(() => {
    fetchBeats();
    seedData();
  }, []);

  const seedData = async () => {
    try {
      await axios.post(`${API}/seed`);
    } catch (e) {}
  };

  const fetchBeats = async () => {
    try {
      const response = await axios.get(`${API}/beats`);
      setBeats(response.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        {/* Background with 23 LV-style monogram pattern */}
        <div className="absolute inset-0 z-0">
          {/* Dark gradient base */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#080808] via-[#0A0A0A] to-[#080808]" />
          
          {/* LV-style repeating 23 monogram pattern - MORE VISIBLE */}
          <div 
            className="absolute inset-0 opacity-[0.15]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'%3E%3Ctext x='10' y='40' font-family='serif' font-size='32' font-weight='bold' fill='%23D4AF37'%3E23%3C/text%3E%3Ctext x='70' y='90' font-family='serif' font-size='32' font-weight='bold' fill='%23D4AF37' transform='rotate(180 85 75)'%3E23%3C/text%3E%3C/svg%3E")`,
              backgroundSize: '120px 120px',
            }}
          />
          
          {/* Second layer - offset pattern for density */}
          <div 
            className="absolute inset-0 opacity-[0.12]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'%3E%3Ctext x='40' y='70' font-family='serif' font-size='24' font-weight='bold' fill='%23D4AF37'%3E23%3C/text%3E%3C/svg%3E")`,
              backgroundSize: '120px 120px',
              backgroundPosition: '60px 60px',
            }}
          />
          
          {/* Diagonal version for more LV feel */}
          <div 
            className="absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'%3E%3Ctext x='20' y='45' font-family='serif' font-size='20' font-weight='bold' fill='%23FFD700' transform='rotate(-45 40 40)'%3E23%3C/text%3E%3C/svg%3E")`,
              backgroundSize: '80px 80px',
            }}
          />
          
          {/* Center glow for depth */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#D4AF37] opacity-[0.05] blur-[200px] rounded-full" />
          
          {/* Subtle vignette effect - reduced */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-[#050505] opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505] opacity-30" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-32">
          <div className="max-w-3xl">
            <div className="inline-block mb-6 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <span className="text-[#D4AF37] text-sm font-medium">Premium Beats & Engineering</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-tight mb-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <span className="brand-font gold-shimmer text-8xl sm:text-9xl lg:text-[12rem] animate-float">23</span>
            </h1>
            
            <p className="text-xl text-neutral-400 mb-10 max-w-xl animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              Premium beats crafted for artists who refuse to blend in. Plus professional mixing & mastering to make your sound shine.
            </p>

            <div className="flex flex-wrap gap-4 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
              {sectionVisibility?.beats && (
                <button 
                  onClick={() => navigate('/beats')}
                  className="btn-primary flex items-center gap-2"
                  data-testid="browse-beats-btn"
                >
                  <Music size={18} />
                  Browse Beats
                </button>
              )}
              {sectionVisibility?.services && (
                <button 
                  onClick={() => navigate('/services')}
                  className="btn-secondary flex items-center gap-2"
                  data-testid="view-services-btn"
                >
                  <Headphones size={18} />
                  View Services
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Floating stats */}
        <div className="absolute bottom-20 left-0 right-0 z-10 hidden lg:block">
          <div className="max-w-7xl mx-auto px-12">
            <div className="flex gap-12">
              <div>
                <p className="text-4xl font-bold gradient-text" style={{ fontFamily: 'Syne' }}>100+</p>
                <p className="text-neutral-500 text-sm">Premium Beats</p>
              </div>
              <div>
                <p className="text-4xl font-bold gradient-text" style={{ fontFamily: 'Syne' }}>500+</p>
                <p className="text-neutral-500 text-sm">Happy Artists</p>
              </div>
              <div>
                <p className="text-4xl font-bold gradient-text" style={{ fontFamily: 'Syne' }}>3</p>
                <p className="text-neutral-500 text-sm">License Options</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Beats */}
      {sectionVisibility?.beats && (
        <section className="py-24 px-6 lg:px-12" data-testid="featured-beats-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-12">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold mb-2 text-white" style={{ fontFamily: 'Syne' }}>Featured Beats</h2>
                <p className="text-neutral-500">Hand-picked heat from the catalog</p>
              </div>
              <Link to="/beats" className="text-[#D4AF37] hover:text-[#FFD700] flex items-center gap-1 text-sm font-medium">
                View All <ChevronRight size={16} />
              </Link>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="glass-card p-4 animate-pulse">
                    <div className="aspect-square rounded-xl bg-neutral-800 mb-4" />
                    <div className="h-5 bg-neutral-800 rounded mb-2 w-3/4" />
                    <div className="h-4 bg-neutral-800 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {beats.slice(0, 4).map(beat => (
                  <BeatCard key={beat.beat_id} beat={beat} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Services Preview */}
      {sectionVisibility?.services && (
        <section className="py-24 px-6 lg:px-12 bg-[#0A0A0A]" data-testid="services-preview">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-white" style={{ fontFamily: 'Syne' }}>
                  Services & <span className="gradient-text">Courses</span>
                </h2>
                <p className="text-neutral-400 text-lg mb-8">
                  Take your sound to the next level with professional mixing and mastering. 
                  Or learn the sauce yourself with our mixing courses.
                </p>
                
                <div className="space-y-4 mb-8">
                  {[
                    'Professional mixing & mastering services',
                    'Mix Course - Learn the techniques',
                    'Lifetime access to new content & sauce',
                    'Live 1-on-1 coaching sessions'
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                        <Check size={14} className="text-[#D4AF37]" />
                      </div>
                      <span className="text-neutral-300">{item}</span>
                    </div>
                  ))}
                </div>

                <button 
                  onClick={() => navigate('/services')}
                  className="btn-primary"
                  data-testid="explore-services-btn"
                >
                  Explore Services & Courses
                </button>
              </div>

              <div className="relative">
                <div className="glass-card p-8 relative z-10">
                  <img 
                    src="https://images.unsplash.com/photo-1650147880756-32cff42ac2d7?w=600&q=80" 
                    alt="Mixing console"
                    className="rounded-xl w-full"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 w-full h-full bg-gradient-to-br from-[#D4AF37]/20 to-[#FFD700]/10 rounded-2xl -z-0" />
              </div>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default HomePage;
